<template>
  <div class="field-loop-configuration">
    <div class="field-header my-3">
      <label class="field-label">
        <i class="fas fa-repeat loop-icon fa-fw" />
        {{ t('templateBuilder.loop.title') }}
      </label>
      <div class="header-actions">
        <Button
          :label="isConfigured ? t('templateBuilder.loop.modify') : t('templateBuilder.loop.configure')"
          :icon="isConfigured ? 'far fa-edit' : 'far fa-plus'"
          class="p-button-sm p-button-text btn-action-green"
          @click="openConfiguration"
        />
        <Button
          v-if="isConfigured"
          icon="far fa-trash-alt"
          class="p-button-sm p-button-secondary p-button-danger"
          @click="deleteConfiguration"
        />
      </div>
    </div>

    <div
      v-if="isConfigured"
      class="loop-summary"
    >
      <div class="loop-info">
        <i class="far fa-check-circle text-success mr-2" />
        <div class="loop-details">
          <div class="loop-main">
            <strong>{{ modelValue?.collection }}</strong> ({{ modelValue?.iterator }})
          </div>
          <div
            v-if="modelValue?.order || modelValue?.limit"
            class="loop-params"
          >
            <span
              v-if="modelValue?.order"
              class="param-badge"
            >
              <i class="far fa-sort mr-1" />
              {{ modelValue.order.field }} ({{ modelValue.order.direction === 'asc' ? '↑' : '↓' }})
            </span>
            <span
              v-if="modelValue?.limit"
              class="param-badge"
            >
              <i class="far fa-list-ol mr-1" />
              {{ modelValue.limit }} {{ t('templateBuilder.loop.results') }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="no-loop"
    >
      <i class="far fa-info-circle text-muted mr-2" />
      <span class="text-muted">{{ t('templateBuilder.loop.noLoopConfigured') }}</span>
    </div>

    <LoopConfigurationModal
      v-model:visible="showDialog"
      :model-value="currentLoopValue"
      :title="t('templateBuilder.loop.configureLoop')"
      :show-delete-button="false"
      @save="handleLoopSave"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent, PropType, ref, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import LoopConfigurationModal from '@/components/template-builder/modals/LoopConfigurationModal.vue';
import type { LoopConfiguration } from '@/types/loop-configuration';
import { FieldConfig, ParserFieldObject } from '@/types';

export default defineComponent({
  name: 'FormFieldLoopConfiguration',

  components: {
    Button,
    LoopConfigurationModal,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: ['on-change-properties'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const showDialog = ref(false);

    const property = computed(() => props.parserValues.properties?.[0]);
    const modelValue = computed(() => property.value?.value as LoopConfiguration | null);

    const isConfigured = computed(() => modelValue.value?.enabled
      && modelValue.value?.collection
      && modelValue.value?.iterator);

    const currentLoopValue = computed(() => {
      if (modelValue.value?.enabled) {
        return modelValue.value;
      }
      return null;
    });

    const openConfiguration = () => {
      showDialog.value = true;
    };

    const handleLoopSave = (config: LoopConfiguration) => {
      if (property.value) {
        const updatedProperty = { ...property.value, value: config };

        emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [updatedProperty],
        });
      }
      showDialog.value = false;
    };

    const deleteConfiguration = () => {
      if (property.value) {
        const updatedProperty = { ...property.value, value: null };

        emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [updatedProperty],
        });
      }
    };

    return {
      t,
      showDialog,
      isConfigured,
      modelValue,
      currentLoopValue,
      openConfiguration,
      handleLoopSave,
      deleteConfiguration,
    };
  },
});
</script>

<style scoped lang="scss">
.field-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .field-label {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 0.875rem;

    .loop-icon {
      margin-right: 8px;
      font-size: 1.125rem;
      color: #06b6d4;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
}

.loop-summary {
  background: rgba(6, 182, 212, 0.1);
  border: 1px solid rgba(6, 182, 212, 0.5);
  border-radius: 6px;
  padding: 0.75rem 1rem;

  .loop-info {
    display: flex;
    align-items: flex-start;
    font-size: 0.875rem;
    color: rgba(6, 182, 212, 1);
    font-weight: 500;
  }

  .loop-details {
    flex: 1;
  }

  .loop-main {
    margin-bottom: 0.5rem;
  }

  .loop-params {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .param-badge {
    display: inline-flex;
    align-items: center;
    background: rgba(6, 182, 212, 0.2);
    border: 1px solid rgba(6, 182, 212, 0.3);
    border-radius: 4px;
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    font-weight: 500;
  }
}

.no-loop {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background: var(--surface-0);
  border: 1px dashed var(--surface-300);
  border-radius: 6px;
  font-size: 0.875rem;
}

.loop-config-form {
  .field {
    margin-bottom: 1rem;
  }
}

.loop-preview {
  border: 1px solid #06b6d4;
}

.custom-icon {
  color: #8b5cf6;
  font-size: 0.75rem;
}

.custom-badge {
  background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
  color: white;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.15rem 0.4rem;
  border-radius: 0.25rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-left: 0.5rem;
}

.btn-action-green {
  color: #94c840 !important;
}
</style>
